import React, { useState } from "react"

import dayjs from "dayjs"
import { RouteComponentProps, useHistory } from "react-router-dom"
import { gql, useQuery } from "urql"

import {
  ChartBarIcon,
  UserCircleIcon,
  UserGroupIcon,
} from "@heroicons/react/20/solid"

import { Color, ValueFormat } from "~tailwindui/types/enums"

import AlertMessageBox from "~tailwindui/Basics/AlertMessageBox"
import WithLoadingIndicator from "~tailwindui/Basics/WithLoadingIndicator"
import ChipDetails from "~tailwindui/ChipDetails"
import Table from "~tailwindui/Table/Table"
import { chipIconColorClassNames } from "~tailwindui/helpers/classNameHelpers"

import { MatchParams } from "src/types/MatchParams"

import LocalTime from "components/LocalTime"
import DataDateRange from "components/OrganizationAdmin/components/DataDateRange"
import Header from "components/OrganizationAdmin/components/DisplayableFormikForm/Header"
import ExportDropDown from "components/OrganizationAdmin/components/ExportDropDown"
import GroupedBarChart from "components/OrganizationAdmin/components/GroupedBarChart"

const SchoolDetails: React.FC<RouteComponentProps<MatchParams>> = ({
  match: {
    params: { id },
  },
}) => {
  const history = useHistory()
  const defaultStartingAt = dayjs().startOf("year")
  const defaultEndingAt = dayjs()
  const [startingAt, setStartingAt] = useState(defaultStartingAt)
  const [endingAt, setEndingAt] = useState(defaultEndingAt)

  const [studyGroupsPage, setStudyGroupsPage] = useState(1)
  const [studentsPage, setStudentsPage] = useState(1)
  const [schoolsResult] = useQuery({
    query: schoolsQuery,
    variables: { id, startingAt, endingAt },
  })

  const [studentsResult] = useQuery({
    query: studentsQuery,
    variables: { id, studyGroupsPage, studentsPage, startingAt, endingAt },
  })

  return (
    <>
      <WithLoadingIndicator result={schoolsResult}>
        {({ data: { school } }) => <Header title={school?.name} />}
      </WithLoadingIndicator>
      <DataDateRange
        startDate={startingAt}
        setStartDate={setStartingAt}
        endDate={endingAt}
        setEndDate={setEndingAt}
      />
      <WithLoadingIndicator result={schoolsResult}>
        {({ data: { viewer, school } }) =>
          school === null ? (
            <AlertMessageBox>
              You do not have access to this school
            </AlertMessageBox>
          ) : (
            <>
              <ChipDetails
                details={[
                  {
                    label: "Attendance",
                    Icon: ChartBarIcon,
                    iconClassNames: chipIconColorClassNames(Color.Green),
                    stats: [
                      {
                        label: "Groups",
                        value: school.groupAttendance,
                        valueFormat: ValueFormat.Percent,
                      },
                      {
                        label: "Students",
                        value: school.studentAttendance,
                        valueFormat: ValueFormat.Percent,
                      },
                    ],
                  },
                  {
                    label: "No Show Sessions",
                    Icon: UserCircleIcon,
                    iconClassNames: chipIconColorClassNames(Color.Red),
                    stats: [
                      {
                        label: "Group",
                        value: school.noShowStudyGroupsCount,
                        valueFormat: ValueFormat.Number,
                      },
                      {
                        label: "Student",
                        value: school.noShowStudentsCount,
                        valueFormat: ValueFormat.Number,
                      },
                    ],
                  },
                  {
                    label: "Active",
                    Icon: UserGroupIcon,
                    iconClassNames: chipIconColorClassNames(Color.Blue),
                    stats: [
                      {
                        label: "Groups",
                        value: school.activeStudyGroupsCount,
                        valueFormat: ValueFormat.Number,
                      },
                      {
                        label: "Students",
                        value: school.activeStudentsCount,
                        valueFormat: ValueFormat.Number,
                      },
                    ],
                  },
                ]}
              />

              <ExportDropDown
                startingAt={startingAt}
                endingAt={endingAt}
                timezone={viewer.emailTimeZone}
                schools={[school]}
              />

              <hr />

              <div className="mt-8">
                <GroupedBarChart
                  query={subjectTypeQuery}
                  variables={{
                    startingAt,
                    endingAt,
                    schoolIds: [school.id],
                  }}
                  dataSetKey="sessionsDataSet"
                  groupByField="subjectBucketsSubjectType"
                  title="Sessions per Subject Type"
                />
              </div>
            </>
          )
        }
      </WithLoadingIndicator>

      <WithLoadingIndicator result={studentsResult}>
        {({ data: { school } }) =>
          school && (
            <>
              <Table
                title="Active Study Groups"
                data={school.studyGroupsByNoShow}
                changePage={setStudyGroupsPage}
                rowCallback={({ studyGroup }) =>
                  history.push(`/study_groups/${studyGroup.id}`)
                }
                cols={[
                  {
                    header: "Name",
                    value: ({ studyGroup }) => studyGroup.name,
                  },
                  {
                    header: "Subject",
                    value: ({ studyGroup }) => (
                      <ul key="subjectsList" className="list-none">
                        {studyGroup.subjects.map(subject => (
                          <li key={subject.id}>{subject.name}</li>
                        ))}
                      </ul>
                    ),
                  },
                  {
                    header: "Students",
                    value: ({ studyGroup }) => (
                      <ul key="studentsList" className="list-none">
                        {studyGroup.students.map(student => (
                          <li key={student.id}>{student.fullName}</li>
                        ))}
                      </ul>
                    ),
                  },
                  { header: "No Shows", value: record => record.noShows },
                ]}
              />

              <Table
                title="Active Students"
                data={school.students}
                changePage={setStudentsPage}
                rowCallback={student => history.push(`/students/${student.id}`)}
                cols={[
                  { header: "First Name", value: student => student.firstName },
                  { header: "Last Name", value: student => student.lastName },
                  { header: "Grade", value: student => student.grade?.name },
                  {
                    header: "No Shows",
                    value: student => student.rangedNoShowCount,
                  },
                  {
                    header: "Next Session",
                    value: student => (
                      <LocalTime
                        key="startsAt"
                        timestamp={student.nextSession?.startsAt}
                      />
                    ),
                  },
                ]}
              />
            </>
          )
        }
      </WithLoadingIndicator>
    </>
  )
}

const schoolsQuery = gql`
  query schoolDetails($id: ID!, $startingAt: DateTime!, $endingAt: DateTime!) {
    viewer {
      emailTimeZone
    }
    school(id: $id) {
      id
      name
      studentAttendance: attendance(
        startingAt: $startingAt
        endingAt: $endingAt
        groupRate: false
      )
      groupAttendance: attendance(
        startingAt: $startingAt
        endingAt: $endingAt
        groupRate: true
      )
      activeStudentsCount: studentsCount(
        startDate: $startingAt
        endDate: $endingAt
        active: true
      )
      activeStudyGroupsCount: studyGroupsCount(
        startDate: $startingAt
        endDate: $endingAt
        active: true
      )
      noShowStudentsCount: noShows(startDate: $startingAt, endDate: $endingAt)
      noShowStudyGroupsCount: noShows(
        startDate: $startingAt
        endDate: $endingAt
        group: true
      )
      startingGrade {
        id
        name
      }
      endingGrade {
        id
        name
      }
    }
  }
`

const subjectTypeQuery = gql`
  query MonthlySessionsGroupedByMonthAndSubjectType(
    $startingAt: DateTime!
    $endingAt: DateTime!
    $schoolIds: [ID!]
  ) {
    viewer {
      organization {
        sessionsDataSet(
          filters: {
            startingAt: $startingAt
            endingAt: $endingAt
            schoolIds: $schoolIds
          }
          groupByOptions: {
            groups: [subjectBucketsSubjectType]
            timePeriod: { periodLength: month, dateField: startsAt }
          }
        ) {
          total
          groupedData {
            value
            groupingStartDate
            subjectBucketsSubjectType
          }
        }
      }
    }
  }
`

const studentsQuery = gql`
  query students(
    $id: ID!
    $studyGroupsPage: Int!
    $studentsPage: Int!
    $startingAt: DateTime!
    $endingAt: DateTime!
  ) {
    school(id: $id) {
      studyGroupsByNoShow(
        page: $studyGroupsPage
        startDate: $startingAt
        endDate: $endingAt
      ) {
        currentPage
        totalPages
        data {
          noShows
          studyGroup {
            id
            name
            subjects {
              id
              name
            }
            students {
              id
              fullName
            }
          }
        }
      }
      students(
        page: $studentsPage
        startDate: $startingAt
        endDate: $endingAt
      ) {
        currentPage
        totalPages
        data {
          id
          firstName
          lastName
          grade {
            id
            name
          }
          rangedNoShowCount(startDate: $startingAt, endDate: $endingAt)
          nextSession {
            id
            startsAt
          }
        }
      }
    }
  }
`

export default SchoolDetails
